import { useCallback, useRef, useState } from "react";
import type { TouchEvent as ReactTouchEvent } from "react";

/**
 * useSwipeReveal — detects horizontal swipe gestures on a row-like element
 * and fires `onSwipe` once the travel crosses a threshold. While the finger
 * is down and moving horizontally, the hook reports `translateX` so the caller
 * can translate the row for visual feedback. If the gesture ends before the
 * threshold is hit, the row animates back to 0 and `onSwipe` is not called.
 *
 * Works in both directions: swipe left OR right both trigger the action.
 *
 * The hook also tracks whether a swipe was in progress so callers can suppress
 * the row's click handler when the gesture ends past the threshold (otherwise
 * the tap that ended the swipe would also open expand/detail views).
 */

export interface UseSwipeRevealOptions {
  /** Fired when the user completes a swipe past the threshold. */
  onSwipe: () => void;
  /** Minimum horizontal travel (px) before the gesture is considered a swipe. */
  threshold?: number;
  /** Max |translateX| applied during drag for visual feedback. */
  maxReveal?: number;
  /**
   * Vertical-to-horizontal travel ratio above which we treat the gesture as a
   * vertical scroll instead of a swipe (so the list can still scroll). A value
   * of 0.5 means: if vertical travel is more than half the horizontal travel,
   * abort the swipe.
   */
  verticalSlopRatio?: number;
}

export interface UseSwipeRevealHandlers {
  onTouchStart: (e: ReactTouchEvent<HTMLElement>) => void;
  onTouchMove: (e: ReactTouchEvent<HTMLElement>) => void;
  onTouchEnd: (e: ReactTouchEvent<HTMLElement>) => void;
  onTouchCancel: (e: ReactTouchEvent<HTMLElement>) => void;
}

export interface UseSwipeRevealResult {
  handlers: UseSwipeRevealHandlers;
  /** Current translateX in px (0 when idle). Apply as `transform: translateX(${translateX}px)`. */
  translateX: number;
  /**
   * Returns true if a swipe just finished past the threshold. Callers should
   * check this inside their row onClick to suppress the click.
   */
  consumeClickSuppression: () => boolean;
}

export function useSwipeReveal(options: UseSwipeRevealOptions): UseSwipeRevealResult {
  const {
    onSwipe,
    threshold = 56,
    maxReveal = 88,
    verticalSlopRatio = 0.6
  } = options;

  const startXRef = useRef<number | null>(null);
  const startYRef = useRef<number | null>(null);
  const lastDxRef = useRef<number>(0);
  const abortedRef = useRef<boolean>(false);
  const suppressClickRef = useRef<boolean>(false);
  const [translateX, setTranslateX] = useState<number>(0);

  const reset = useCallback(() => {
    startXRef.current = null;
    startYRef.current = null;
    lastDxRef.current = 0;
    abortedRef.current = false;
    setTranslateX(0);
  }, []);

  const onTouchStart = useCallback((e: ReactTouchEvent<HTMLElement>) => {
    const t = e.touches[0];
    if (!t) return;
    startXRef.current = t.clientX;
    startYRef.current = t.clientY;
    lastDxRef.current = 0;
    abortedRef.current = false;
  }, []);

  const onTouchMove = useCallback(
    (e: ReactTouchEvent<HTMLElement>) => {
      if (startXRef.current == null || startYRef.current == null) return;
      if (abortedRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startXRef.current;
      const dy = t.clientY - startYRef.current;

      // If the gesture is clearly vertical, abort so the list can scroll.
      if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * verticalSlopRatio) {
        abortedRef.current = true;
        lastDxRef.current = 0;
        setTranslateX(0);
        return;
      }

      // Clamp horizontal travel to maxReveal in both directions for feedback.
      const clamped = Math.max(-maxReveal, Math.min(maxReveal, dx));
      lastDxRef.current = dx;
      setTranslateX(clamped);
    },
    [maxReveal, verticalSlopRatio]
  );

  const onTouchEnd = useCallback(
    (_e: ReactTouchEvent<HTMLElement>) => {
      if (startXRef.current == null) {
        reset();
        return;
      }
      const dx = lastDxRef.current;
      const aborted = abortedRef.current;
      reset();
      if (!aborted && Math.abs(dx) >= threshold) {
        suppressClickRef.current = true;
        onSwipe();
      }
    },
    [onSwipe, reset, threshold]
  );

  const onTouchCancel = useCallback(
    (_e: ReactTouchEvent<HTMLElement>) => {
      reset();
    },
    [reset]
  );

  const consumeClickSuppression = useCallback(() => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return true;
    }
    return false;
  }, []);

  return {
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    translateX,
    consumeClickSuppression
  };
}
